// src/features/tasks/hooks/useTaskTracking.js
import { useState, useCallback } from 'react';
import { taskTrackingAPI } from '../services/taskTracking';
import { useApi } from '../../../hooks/api/useApi';

export function useTaskTracking() {
  const [taskHistory, setTaskHistory] = useState([]);
  const [taskActivitySummary, setTaskActivitySummary] = useState(null);
  const [taskRecurrenceHistory, setTaskRecurrenceHistory] = useState(null);
  const [departmentActivity, setDepartmentActivity] = useState(null);
  const [staffActivity, setStaffActivity] = useState([]);
  const [trackingStatistics, setTrackingStatistics] = useState(null);
  const { loading, error, execute, clearError } = useApi();

  // Task-specific tracking
  const fetchTaskTrackingHistory = useCallback(async (taskId, params = {}) => {
    const result = await execute(() => taskTrackingAPI.getTaskTrackingHistory(taskId, params));
    if (result.success) {
      setTaskHistory(result.data.data?.tracking_entries || []);
    }
    return result;
  }, [execute]);

  const fetchTaskActivitySummary = useCallback(async (taskId) => {
    const result = await execute(() => taskTrackingAPI.getTaskActivitySummary(taskId));
    if (result.success) {
      setTaskActivitySummary(result.data.data);
    }
    return result;
  }, [execute]);

  const fetchTaskRecurrenceHistory = useCallback(async (taskId, params = {}) => {
    const result = await execute(() => taskTrackingAPI.getTaskRecurrenceHistory(taskId, params));
    if (result.success) {
      setTaskRecurrenceHistory(result.data.data);
    }
    return result;
  }, [execute]);

  // Department tracking
  const fetchRecentDepartmentActivity = useCallback(async (params = {}) => {
    const result = await execute(() => taskTrackingAPI.getRecentDepartmentActivity(params));
    if (result.success) {
      setDepartmentActivity(result.data.data);
    }
    return result;
  }, [execute]);

  // Staff tracking
  const fetchStaffMemberActivity = useCallback(async (staffMemberId, params = {}) => {
    const result = await execute(() => taskTrackingAPI.getStaffMemberActivity(staffMemberId, params));
    if (result.success) {
      setStaffActivity(result.data.data?.tracking_entries || []);
    }
    return result;
  }, [execute]);

  const fetchMyActivity = useCallback(async (params = {}) => {
    const result = await execute(() => taskTrackingAPI.getMyActivity(params));
    if (result.success) {
      setStaffActivity(result.data.data?.tracking_entries || []);
    }
    return result;
  }, [execute]);

  // Statistics
  const fetchTrackingStatistics = useCallback(async (params = {}) => {
    const result = await execute(() => taskTrackingAPI.getTrackingStatistics(params));
    if (result.success) {
      setTrackingStatistics(result.data.data);
    }
    return result;
  }, [execute]);

  // Clear all data
  const clearTrackingData = useCallback(() => {
    setTaskHistory([]);
    setTaskActivitySummary(null);
    setTaskRecurrenceHistory(null);
    setDepartmentActivity(null);
    setStaffActivity([]);
    setTrackingStatistics(null);
  }, []);

  return {
    // Data
    taskHistory,
    taskActivitySummary,
    taskRecurrenceHistory,
    departmentActivity,
    staffActivity,
    trackingStatistics,
    
    // State
    loading,
    error,
    clearError,
    
    // Actions
    fetchTaskTrackingHistory,
    fetchTaskActivitySummary,
    fetchTaskRecurrenceHistory,
    fetchRecentDepartmentActivity,
    fetchStaffMemberActivity,
    fetchMyActivity,
    fetchTrackingStatistics,
    clearTrackingData,
  };
}